import * as React from 'react';
import { Component } from 'react';
import { parse } from 'query-string';
import Lazy from 'lazy.js';
import propz from 'propz';
import BPromise from 'bluebird';
import { History, Location } from 'history';
import { AVAILABLE_SCHOOL_TYPE } from '../../../../../consts/school';
import { CreateGeneralMessageButtons } from '../../../../../components/CreateGeneralMessageButtons/CreateGeneralMessageButtons';
import { ProgressBar } from '../../../../../components/ProgressBar/ProgressBar';
import { Loader } from '../../../../../components/Loader/Loader';
import { Button } from '../../../../../components/Button/Button';
import {
  HTML_FIRST_PART_PAGE_CODE,
  HTML_LAST_PART_PAGE_CODE
} from '../../../../../components/HTMLEditorQuill/HTMLEditorConst';
import { getTournament } from '../../../../../helpers/service/adminSU/tournamentsTable';
import {
  createTournamentNotification,
  sendTournamentNotificationNow,
  scheduleTournamentNotification
} from '../../../../../helpers/service/adminSU/tournamentNotificationTable';
import { getAvailableSchools } from '../../../../../helpers/service/adminSU/schoolsTable';
import { getSettingsUploadFiles } from '../../../../../helpers/service/nobody/settings';
import {
  DEFAULT_GENERAL_MESSAGE_ATTACHMENT_SIZE_LIMIT_BYTES,
  formatGeneralMessageAttachmentSize,
  normalizeGeneralMessageAttachmentSizeLimit
} from '../../../../../helpers/generalMessageAttachments/generalMessageAttachmentSize';
import { uploadFileAllPath } from '../../../../../helpers/service/file';
import { Attachment } from '../../../../../models/generalMessage';
import { Tournament } from '../../../../../models/tournament';
import { AppUser } from '../../../../../views/App/App';
import { TournamentCustomNotificationTextForm } from './TournamentCustomNotificationTextForm';
import { TournamentCustomNotifications } from '../TournamentCustomNotifications';
import { TournamentCustomNotificationPreview } from './TournamentCustomNotificationPreview';
import '../TournamentNotificationWizard.scss';

enum MESSAGE_CREATING_STEPS {
  SCHOOL_LIST = 'SCHOOL_LIST',
  CONTENT = 'CONTENT',
  PREVIEW = 'PREVIEW'
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface MessageState {
  messageEmailSubject: string;
  messageEmailBody: string;
  messagePushTitle: string;
  messagePushBody: string;
  availableSchools: any[];
  updatedAvailableSchools: any[];

  currentStep: number;
  isError: boolean;
  errorMessage: string;
  tournament: Tournament | undefined;

  messageAttachments: Attachment[];
  isImageError: boolean;
  tournamentId: string;
  isLoading: boolean;
  filterText: string;
  isSelectAll: boolean;
  isSelectTournamentParticipant: boolean;
  isSelectTournamentWaitingList: boolean;
  filesSize: number;
  maxSizeUploadFile: number;

  isLoadingFile: boolean;
  isPushEditorEnabled: boolean;
  isEmailEditorEnabled: boolean;

  includeUnionAdmins: boolean;

  submitAction: 'SEND_NOW' | 'SCHEDULE';
  scheduleAt: string;
}

export class CreateTournamentCustomNotificationsView extends Component<Props, MessageState> {
  constructor(props: Props) {
    super(props);

    this.state = {
      currentStep: 0,
      isError: false,
      errorMessage: '',
      messageEmailSubject: '',
      messageEmailBody: '',
      messagePushTitle: '',
      messagePushBody: '',
      tournament: undefined,
      messageAttachments: [],
      isImageError: false,
      availableSchools: [],
      tournamentId: '',
      isLoading: false,
      filterText: '',
      updatedAvailableSchools: [],
      isSelectAll: false,
      isSelectTournamentParticipant: false,
      isSelectTournamentWaitingList: false,
      filesSize: 0,
      maxSizeUploadFile: DEFAULT_GENERAL_MESSAGE_ATTACHMENT_SIZE_LIMIT_BYTES,
      isLoadingFile: false,
      isPushEditorEnabled: true,
      isEmailEditorEnabled: true,
      includeUnionAdmins: true,
      submitAction: 'SEND_NOW',
      scheduleAt: ''
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setAdditionalItems().then(() => {
      this.setItems();
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      const tournamentId = this.getTournamentId();
      this.setState({
        tournamentId
      });
    }
  }

  setAdditionalItems() {
    const { user } = this.props;

    return getSettingsUploadFiles(user)
      .then(maxSizeUploadFile => {
        this.setState({
          maxSizeUploadFile
        });

        return true;
      })
      .catch(error => {
        console.error(error);
        return true;
      });
  }

  setItems() {
    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    return BPromise.all([getTournament(user, tournamentId), getAvailableSchools(user, tournamentId)]).then(
      ([tournament, availableSchools]) => {
        const transformAvailableSchools = Lazy(availableSchools)
          .map(school => ({
            schoolId: school.schoolId,
            schoolName: school.schoolName,
            type: school.type,
            checked: false
          }))
          .flatten()
          .uniq('schoolId')
          .toArray();

        this.setState({
          tournament,
          availableSchools: transformAvailableSchools,
          updatedAvailableSchools: transformAvailableSchools,
          isLoading: false
        });

        return true;
      }
    );
  }

  getTournamentId(): string {
    const { history } = this.props;
    const search = parse(history.location.search);
    return search.tournament as string;
  }

  onItemCheck = nextItems => {
    this.setState({
      updatedAvailableSchools: nextItems,
      availableSchools: nextItems
    });
  };

  onUpdateItemsEmptyFilterText = text => {
    this.setState({
      updatedAvailableSchools: this.state.availableSchools,
      filterText: text,
      isSelectAll: false,
      isSelectTournamentParticipant: false,
      isSelectTournamentWaitingList: false
    });
  };

  onUpdateItemsFilterText = (itemsFiltered, text) => {
    this.setState({
      updatedAvailableSchools: itemsFiltered,
      filterText: text
    });
  };

  onSelectAllClick = () => {
    const { isSelectAll, updatedAvailableSchools } = this.state;

    const newItems = updatedAvailableSchools.map(item => ({
      ...item,
      checked: !isSelectAll
    }));

    this.setState({
      isSelectAll: !isSelectAll,
      isSelectTournamentParticipant: !isSelectAll,
      isSelectTournamentWaitingList: !isSelectAll,
      updatedAvailableSchools: newItems
    });
  };

  onSelectTournamentParticipantClick = (): void => {
    const { isSelectTournamentParticipant, updatedAvailableSchools } = this.state;

    const newItems = updatedAvailableSchools.map(item => {
      if (item.type === AVAILABLE_SCHOOL_TYPE.TOURNAMENT_PARTICIPANT) {
        return {
          ...item,
          checked: !isSelectTournamentParticipant
        };
      }

      return { ...item };
    });

    this.setState({
      isSelectTournamentParticipant: !isSelectTournamentParticipant,
      updatedAvailableSchools: newItems
    });
  };

  onSelectTournamentWaitingListClick = (): void => {
    const { isSelectTournamentWaitingList, updatedAvailableSchools } = this.state;

    const newItems = updatedAvailableSchools.map(item => {
      if (item.type === AVAILABLE_SCHOOL_TYPE.TOURNAMENT_WAITING_LIST) {
        return {
          ...item,
          checked: !isSelectTournamentWaitingList
        };
      }

      return { ...item };
    });

    this.setState({
      isSelectTournamentWaitingList: !isSelectTournamentWaitingList,
      updatedAvailableSchools: newItems
    });
  };

  clearValidationError = () => {
    if (this.state.isError) {
      this.setState({
        isError: false,
        errorMessage: ''
      });
    }
  };

  onNextClick = () => {
    const {
      currentStep,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      messageEmailBody,
      messageEmailSubject,
      messagePushBody,
      messagePushTitle
    } = this.state;

    const steps = this.getSteps();
    const currentStepName = steps[currentStep];
    const isCurrentStepContent = currentStepName === MESSAGE_CREATING_STEPS.CONTENT;

    const emailBodyEmpty = !messageEmailBody || messageEmailBody === '<p><br></p>' || messageEmailBody.trim() === '';
    const pushBodyEmpty = !messagePushBody || messagePushBody.trim() === '';
    const pushTitleEmpty = !messagePushTitle || messagePushTitle.trim() === '';
    const emailSubjectEmpty = !messageEmailSubject || messageEmailSubject.trim() === '';

    const isShowPushError = (pushBodyEmpty || pushTitleEmpty) && isPushEditorEnabled;
    const isShowEmailError = (emailSubjectEmpty || emailBodyEmpty) && isEmailEditorEnabled;

    if (isCurrentStepContent && (isShowPushError || isShowEmailError)) {
      let errorText = '';

      switch (true) {
        case isShowPushError:
          errorText = 'Title or body for push content should be defined';
          break;
        case isShowEmailError:
          errorText = 'Subject or body for email content should be defined';
          break;
      }

      this.setState({
        isError: true,
        errorMessage: errorText
      });

      return;
    }

    this.setState(prevState => ({
      currentStep: prevState.currentStep + 1,
      isError: false,
      errorMessage: ''
    }));

    const { history } = this.props;
    const { search, ...rest } = history.location;
    history.replace(rest);
  };

  onPrevClick = () => {
    this.setState(prevState => ({
      currentStep: prevState.currentStep - 1,
      isError: false,
      errorMessage: ''
    }));
  };

  onEmailSubjectChange = event => {
    this.setState({
      messageEmailSubject: event.target.value,
      isError: false,
      errorMessage: ''
    });
  };

  onEmailBodyEmptyTextChange = emptyText => {
    this.setState({
      messageEmailBody: emptyText,
      isError: false,
      errorMessage: ''
    });
  };

  onEmailBodyChange = editorState => {
    this.setState({
      messageEmailBody: editorState,
      isError: false,
      errorMessage: ''
    });
  };

  onPushTitleChange = event => {
    this.setState({
      messagePushTitle: event.target.value,
      isError: false,
      errorMessage: ''
    });
  };

  onPushBodyChange = event => {
    this.setState({
      messagePushBody: event.target.value,
      isError: false,
      errorMessage: ''
    });
  };

  onEmailAttachmentChange = (event): void => {
    const { user } = this.props;
    const { filesSize, maxSizeUploadFile } = this.state;
    const attachments = [...this.state.messageAttachments];

    const file = event.target.files[0];
    const currentFileSize = event.target.files[0].size;
    const fileName = file.name;
    const fileNameWithoutSpaces = fileName.split(' ').join('_');
    const formData = new FormData();
    formData.append('file', file, fileNameWithoutSpaces);

    const allFilesSize = filesSize + currentFileSize;

    const safeSizeLimit = normalizeGeneralMessageAttachmentSizeLimit(maxSizeUploadFile);

    if (allFilesSize > safeSizeLimit) {
      const maxSizeUploadFileText = formatGeneralMessageAttachmentSize(safeSizeLimit);

      const errorText = `The message cannot be sent as the total size of attachments is too large.
      Please ensure your attachments don't exceed ${maxSizeUploadFileText} and try sending a message again.`;

      this.setState({
        errorMessage: errorText,
        isError: true
      });

      return;
    }

    this.setState({
      isLoadingFile: true
    });

    uploadFileAllPath(user, formData)
      .then(data => {
        const { key, link } = data;

        attachments.push({
          fileName: fileNameWithoutSpaces,
          link,
          externalFileId: key,
          fileSize: currentFileSize
        });

        this.setState({
          messageAttachments: attachments,
          filesSize: allFilesSize,
          isLoadingFile: false
        });
      })
      .catch(() => {
        this.setState({
          isImageError: true,
          isLoadingFile: false
        });
      });
  };

  onRemoveAttachment = (key: string) => {
    const { filesSize } = this.state;
    const attachments = [...this.state.messageAttachments];
    const attachmentIndex = attachments.findIndex(attachment => attachment.externalFileId === key);
    const attachment = attachments.find(attachment => attachment.externalFileId === key);

    attachments.splice(attachmentIndex, 1);

    const newFileSize = filesSize - propz.get(attachment, ['fileSize'], 0);

    this.setState({
      messageAttachments: attachments,
      filesSize: newFileSize
    });
  };

  onCloseImageError = () => {
    this.setState({
      isImageError: false
    });
  };

  onCloseErrorClick = event => {
    event.preventDefault();

    this.setState({
      isError: false,
      errorMessage: ''
    });
  };

  onShowError = (message: string) => {
    this.setState({
      isError: true,
      errorMessage: typeof message === 'string' ? message : 'Error saving notification'
    });
  };

  getSteps(): MESSAGE_CREATING_STEPS[] {
    return [MESSAGE_CREATING_STEPS.SCHOOL_LIST, MESSAGE_CREATING_STEPS.CONTENT, MESSAGE_CREATING_STEPS.PREVIEW];
  }

  getStepTitle(): string {
    const { currentStep } = this.state;
    const steps = this.getSteps();
    const currentStepName = steps[currentStep];

    switch (currentStepName) {
      case MESSAGE_CREATING_STEPS.SCHOOL_LIST:
        return 'Select schools';
      case MESSAGE_CREATING_STEPS.CONTENT:
        return 'Compose message';
      case MESSAGE_CREATING_STEPS.PREVIEW:
        return 'Preview & send';
      default:
        return 'Create notification';
    }
  }

  getAttachmentAsLinksString = () => {
    const { messageAttachments } = this.state;

    if (messageAttachments.length > 0) {
      const attachmentAsLink = messageAttachments
        .map((attachment, index) => {
          return `<div key='attachment_${index}'><a href=${attachment.link} target="_blank">${attachment.fileName}</a></div>`;
        })
        .join('');

      return `<div>Attachments: ${attachmentAsLink}</div>`;
    }

    return '';
  };

  onIsEmailEditorChange = event => {
    const isEmailEditorEnabled = event.target.checked;

    if (!isEmailEditorEnabled) {
      this.setState({
        messageEmailSubject: '',
        messageEmailBody: '',
        messageAttachments: [],
        isEmailEditorEnabled,
        isError: false,
        errorMessage: ''
      });
      return;
    }

    this.setState({
      isEmailEditorEnabled,
      isError: false,
      errorMessage: ''
    });
  };

  onIsPushEditorChange = event => {
    const isPushEditorEnabled = event.target.checked;

    if (!isPushEditorEnabled) {
      this.setState({
        messagePushBody: '',
        messagePushTitle: '',
        isPushEditorEnabled,
        isError: false,
        errorMessage: ''
      });
      return;
    }

    this.setState({
      isPushEditorEnabled,
      isError: false,
      errorMessage: ''
    });
  };

  onSubmitCustomNotificationsForm = () => {
    const {
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      tournament,
      updatedAvailableSchools,
      includeUnionAdmins,
      submitAction,
      scheduleAt,
      messageAttachments
    } = this.state;

    const { user } = this.props;

    const checkedSchoolIds = updatedAvailableSchools.filter(item => item.checked).map(school => school.schoolId);

    const messageEmailBodyWithAttachment = messageEmailBody + this.getAttachmentAsLinksString();
    const messageHTMLEmailBody =
      messageEmailBody !== ''
        ? `${HTML_FIRST_PART_PAGE_CODE} ${messageEmailBodyWithAttachment} ${HTML_LAST_PART_PAGE_CODE}`
        : '';

    const data = {
      schoolIds: checkedSchoolIds,
      title: messageEmailSubject,
      text: messageEmailBody !== '' ? messageHTMLEmailBody : '',
      content: {
        push: {
          title: messagePushTitle,
          body: messagePushBody
        }
      },
      attachments: messageAttachments,
      includeUnionAdmins
    };

    const isScheduleInvalid =
      submitAction === 'SCHEDULE' &&
      (!scheduleAt || Number.isNaN(new Date(scheduleAt).getTime()) || new Date(scheduleAt).getTime() <= Date.now());

    if (isScheduleInvalid) {
      this.onShowError('Please select a future date and time');
      return;
    }

    this.setState({
      isLoading: true
    });

    createTournamentNotification(user, tournament.id, data)
      .then(response => {
        const actionDescriptorId = propz.get(response, ['headers', 'action-descriptor-id']);

        if (!actionDescriptorId) {
          throw new Error('actionDescriptorId is missing');
        }

        if (submitAction === 'SCHEDULE') {
          return scheduleTournamentNotification(user, tournament.id, actionDescriptorId, scheduleAt);
        }

        return sendTournamentNotificationNow(user, tournament.id, actionDescriptorId);
      })
      .then(() => {
        this.goBackTournament(tournament.id);
      })
      .catch(reason => {
        const errorText =
          propz.get(reason, ['response', 'data', 'details', 'text'], '') ||
          propz.get(reason, ['message'], '') ||
          'Error saving notification';

        this.setState({
          isLoading: false
        });

        this.onShowError(errorText);
      });
  };

  goBackTournament = (tournamentId): void => {
    const { history, location } = this.props;
    const { state } = location;
    const { search } = state as any;

    history.push({
      pathname: '/tournaments',
      search,
      state: { tournamentId }
    });
  };

  onCancelClick = () => {
    const { history, location } = this.props;
    const { state } = location;
    const { search } = state as any;
    const tournamentId = this.getTournamentId();

    history.push({
      pathname: '/tournaments',
      search,
      state: { tournamentId }
    });
  };

  isNextButtonDisabled(): boolean {
    const {
      currentStep,
      updatedAvailableSchools,
      isLoadingFile,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      submitAction,
      scheduleAt
    } = this.state;

    const steps = this.getSteps();
    const currentStepName = steps[currentStep];
    const checkedSchools = updatedAvailableSchools.filter(item => item.checked);
    const isAllEditorDisabled = !isPushEditorEnabled && !isEmailEditorEnabled;

    const isEmailInvalid =
      isEmailEditorEnabled &&
      (!messageEmailSubject.trim() ||
        !messageEmailBody ||
        messageEmailBody === '<p><br></p>' ||
        messageEmailBody.trim() === '');

    const isPushInvalid = isPushEditorEnabled && (!messagePushTitle.trim() || !messagePushBody.trim());

    const isScheduleInvalid =
      submitAction === 'SCHEDULE' &&
      (!scheduleAt || Number.isNaN(new Date(scheduleAt).getTime()) || new Date(scheduleAt).getTime() <= Date.now());

    switch (true) {
      case currentStepName === MESSAGE_CREATING_STEPS.SCHOOL_LIST:
        return checkedSchools.length === 0;

      case currentStepName === MESSAGE_CREATING_STEPS.CONTENT:
        return isLoadingFile || isAllEditorDisabled || isEmailInvalid || isPushInvalid;

      case currentStepName === MESSAGE_CREATING_STEPS.PREVIEW:
        return checkedSchools.length === 0 || isScheduleInvalid;

      default:
        return false;
    }
  }

  renderViewByStep(): React.ReactNode {
    const { currentStep } = this.state;
    const steps = this.getSteps();
    const currentStepName = steps[currentStep];

    switch (currentStepName) {
      case MESSAGE_CREATING_STEPS.SCHOOL_LIST:
        return this.renderSchoolList();
      case MESSAGE_CREATING_STEPS.CONTENT:
        return this.renderMessageTextForm();
      case MESSAGE_CREATING_STEPS.PREVIEW:
        return this.renderMessagePreview();
      default:
        return null;
    }
  }

  renderSchoolList(): React.ReactNode {
    const {
      updatedAvailableSchools,
      filterText,
      isSelectAll,
      isSelectTournamentParticipant,
      isSelectTournamentWaitingList,
      tournamentId
    } = this.state;

    return (
      <div className="tTournamentNotificationWizard__step">
        <TournamentCustomNotifications
          user={this.props.user}
          history={this.props.history}
          location={this.props.location}
          availableSchools={updatedAvailableSchools}
          onUpdateItemsEmptyFilterText={this.onUpdateItemsEmptyFilterText}
          onUpdateItemsFilterText={this.onUpdateItemsFilterText}
          onSelectAllClick={this.onSelectAllClick}
          onSelectTournamentParticipantClick={this.onSelectTournamentParticipantClick}
          onSelectTournamentWaitingListClick={this.onSelectTournamentWaitingListClick}
          onItemCheck={this.onItemCheck}
          tournamentId={tournamentId}
          filterText={filterText}
          isSelectAll={isSelectAll}
          isSelectTournamentParticipant={isSelectTournamentParticipant}
          isSelectTournamentWaitingList={isSelectTournamentWaitingList}
        />
      </div>
    );
  }

  renderMessageTextForm(): React.ReactNode {
    const {
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      messageAttachments,
      isImageError,
      isError,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      isLoadingFile
    } = this.state;

    return (
      <div className="tTournamentNotificationWizard__step">
        <TournamentCustomNotificationTextForm
          user={this.props.user}
          onEmailSubjectChange={this.onEmailSubjectChange}
          onEmailBodyChange={this.onEmailBodyChange}
          onEmailBodyEmptyTextChange={this.onEmailBodyEmptyTextChange}
          onPushTitleChange={this.onPushTitleChange}
          onPushBodyChange={this.onPushBodyChange}
          onEmailAttachmentChange={this.onEmailAttachmentChange}
          onRemoveAttachment={this.onRemoveAttachment}
          isImageError={isImageError}
          isError={isError}
          onCloseImageError={this.onCloseImageError}
          messageEmailSubject={messageEmailSubject}
          messageEmailBody={messageEmailBody}
          messagePushTitle={messagePushTitle}
          messagePushBody={messagePushBody}
          messageAttachments={messageAttachments}
          isLoadingFile={isLoadingFile}
          isEmailEditorEnabled={isEmailEditorEnabled}
          isPushEditorEnabled={isPushEditorEnabled}
          onIsEmailEditorChange={this.onIsEmailEditorChange}
          onIsPushEditorChange={this.onIsPushEditorChange}
        />
      </div>
    );
  }

  renderMessagePreview(): React.ReactNode {
    const {
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      messageAttachments,
      updatedAvailableSchools,
      includeUnionAdmins,
      tournament,
      submitAction,
      scheduleAt
    } = this.state;

    const checkedSchools = updatedAvailableSchools.filter(item => item.checked);
    const unionName = (tournament && (tournament.school?.name || (tournament as any).schoolName)) || 'union';

    const isScheduleInvalid =
      submitAction === 'SCHEDULE' &&
      (!scheduleAt || Number.isNaN(new Date(scheduleAt).getTime()) || new Date(scheduleAt).getTime() <= Date.now());

    return (
      <div className="tTournamentNotificationWizard__previewStep">
        <TournamentCustomNotificationPreview
          messageEmailSubject={messageEmailSubject}
          messageEmailBody={messageEmailBody}
          messagePushTitle={messagePushTitle}
          messagePushBody={messagePushBody}
          messageAttachments={messageAttachments}
          isEmailEnabled={this.state.isEmailEditorEnabled}
          isPushEnabled={this.state.isPushEditorEnabled}
          affectedSchools={checkedSchools}
          includeUnionAdmins={includeUnionAdmins}
          unionName={unionName}
          onToggleIncludeUnionAdmins={next => this.setState({ includeUnionAdmins: next })}
          submitAction={submitAction}
          scheduleAt={scheduleAt}
          onSubmitActionChange={next =>
            this.setState({
              submitAction: next,
              isError: false,
              errorMessage: ''
            })
          }
          onScheduleAtChange={next =>
            this.setState({
              scheduleAt: next,
              isError: false,
              errorMessage: ''
            })
          }
          isScheduleInvalid={isScheduleInvalid}
        />
      </div>
    );
  }

  renderSendingError(): React.ReactNode {
    const { errorMessage, isError } = this.state;

    if (!isError) {
      return null;
    }

    return (
      <div className="alert fade show alert-danger d-flex justify-content-between align-items-center" role="alert">
        {errorMessage}
        <button
          type="button"
          className="ml-3 btn btn-danger"
          aria-label="Close"
          onClick={event => this.onCloseErrorClick(event)}
        >
          Close
        </button>
      </div>
    );
  }

  renderProgressBar() {
    const currentStep = this.state.currentStep + 1;
    const totalSteps = this.getSteps().length;
    const progress = (currentStep / totalSteps) * 100;

    return <ProgressBar progress={progress} step={currentStep} stepTotal={totalSteps} />;
  }

  render() {
    const stepCount = this.getSteps().length;
    const { currentStep, isError, isLoading, submitAction, scheduleAt } = this.state;
    const hasNext = currentStep + 1 < stepCount;
    const hasPrev = currentStep > 0;
    const isNextButtonDisabled = this.isNextButtonDisabled();

    const isScheduleInvalid =
      submitAction === 'SCHEDULE' &&
      (!scheduleAt || Number.isNaN(new Date(scheduleAt).getTime()) || new Date(scheduleAt).getTime() <= Date.now());

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="tTournamentNotificationWizard">
        {this.renderSendingError()}

        <div className="tTournamentNotificationWizard__stepHeader">
          <div>
            <h3>{this.getStepTitle()}</h3>
          </div>
          <small>
            Step {currentStep + 1} of {stepCount}
          </small>
        </div>

        {this.renderProgressBar()}
        {this.renderViewByStep()}

        <div className="tTournamentNotificationWizard__actions mt-4">
          <CreateGeneralMessageButtons
            hasNext={hasNext}
            hasPrevious={hasPrev}
            onNextClick={this.onNextClick}
            onPreviousClick={this.onPrevClick}
            onSendClick={() => {}}
            onCancelClick={this.onCancelClick}
            isNextButtonDisabled={isNextButtonDisabled}
            isError={isError}
          />

          {!hasNext && (
            <Button
              onClick={this.onSubmitCustomNotificationsForm}
              text={submitAction === 'SCHEDULE' ? 'Schedule' : 'Send'}
              customClass="btn btn-success"
              disabled={isNextButtonDisabled || isError || isScheduleInvalid}
            />
          )}
        </div>
      </div>
    );
  }
}


